import { withApollo } from '@apollo/react-hoc'
import { VStack, useToast } from '@chakra-ui/react'
import React, { useEffect, useState, useMemo, useRef } from 'react'

import {
  useSendSlackMessageMutation,
  useUpdateSpoofingReportsMutation,
} from '../../generated/graphql'
import {
  getOrgData,
  useEmail,
  useIsEmployee,
  useIsEmployeeView,
  useOrgID,
  useUserID,
} from '../../hooks/id_token_claims'
import {
  useNextSelectedSpoofReport,
  useSelectedPage,
  useSelectedPageSize,
  useSelectedSpoofReport,
} from '../../hooks/spoof_report_table_filters'
import { useSelectedReportStatus, useSelectedReportType } from '../../pages/reports'
import { delayRefetchedQueries } from '../../utils'
import { ReportType } from '../../utils/constants'
import { createTable } from '../../utils/table_utils'
import PaginatedTable from '../tables/paginated_table'
import SpoofReportsTableActions from './spoof_reports_table_actions'
import { sendCustomerReportedBulkSlackMessage } from '../../hooks/slack_notifications'
import {
  AlertTableColumnLabels,
  ProductType,
  ReportChangeEventSource,
} from '@/generated/enums'
import { useAtom } from 'jotai'
import { reportPlatformTabAtom } from '@/hooks/dark_web_platforms'
import {
  ColumnConfig,
  getDarkWebColumns,
  getSpoofingReportColumns,
  getSuspiciousEmailColumns,
} from './spoof_reports_table_content_columns'
import { useRouter } from 'next/router'
import { getDoppelReportUrl } from '@/utils/reports/report_utils'
import { useFeatureFlagEnabled } from 'posthog-js/react'
import { isBulkActionsEnabled } from '@/utils/feature_flags'
import { alertsTableResultAtom } from '@/utils/detailed_view/alerts_table_context'
import { useAppSettings } from '@/utils/app_settings'
import SpoofReportsTableColumnEditDropdown from './spoof_reports_table_column_edit_dropdown'
import { HeaderMenu } from '@/components/web2/alerts_header_menu'

const SpoofReportsTableContent = ({
  data,
  totalPageCount,
  selectedRow,
  setSelectedRow,
  setToRefreshCache,
  client,
  isOrgUnifiedView,
  enableBulkActions = false,
  // uneditable list of columns that are visible (user cannot use the edit button to hide/show columns if these are specified)
  // if not specified all columns will be visible
  fixedVisibleColumns = [],
  sortingMechanism,
  onSortingChange,
  openInNewTab = false,
  style,
}) => {
  const router = useRouter()
  const orgId = useOrgID()
  const [orgData] = getOrgData()
  const [email] = useEmail()
  const [userId] = useUserID()
  const [isEmployeeView] = useIsEmployeeView()
  const isEmployee = useIsEmployee()
  const {
    data: appSettings,
    loading: appSettingsLoading,
    update: updateAppSettings,
  } = useAppSettings()
  const [selectedSpoofReport, setSelectedSpoofReport] = useSelectedSpoofReport()
  const [selectedReportType] = useSelectedReportType()
  const [, setNextSelectedSpoofReport] = useNextSelectedSpoofReport()
  const [selectedPage, setSelectedPage] = useSelectedPage()
  const [selectedPageSize, setSelectedPageSize] = useSelectedPageSize()
  const [actionLoadingState, setActionLoadingState] = useState(null)
  const [isLoadingNextPage, setIsLoadingNextPage] = useState(false)
  const [isLoadingPrevPage, setIsLoadingPrevPage] = useState(false)
  const [sendSlackMessageMutation] = useSendSlackMessageMutation()
  const [selectedReportPlatform, setSelectedReportPlatform] =
    useAtom(reportPlatformTabAtom)
  const filterImprovementsEnabled = useFeatureFlagEnabled('filter-improvements')
  const isComponentMountingRef = useRef(true)

  const [alertsTableResult, setAlertsTableResult] = useAtom(alertsTableResultAtom)

  const [selectedReportStatus] = useSelectedReportStatus()

  const [updateSpoofingReportsMutation] = useUpdateSpoofingReportsMutation()

  const toast = useToast()

  // Only enable bulk actions if both the prop is true and the feature flag allows it for this report type
  const bulkActionsEnabled =
    enableBulkActions && isBulkActionsEnabled(selectedReportType)

  const handleSelectAlert = (alert) => {
    const isSuspiciousEmails =
      alert?.platform_subtype?.product === ProductType.SUSPICIOUS_EMAILS
    const url = getDoppelReportUrl(alert, alert.organization, isSuspiciousEmails)

    if (openInNewTab) {
      window.open(url, '_blank')
    } else {
      router.push(url)
    }
  }

  // wrapper for bulk calling Update Report service, refreshes frontend
  const bulkUpdateReports = (
    rows,
    {
      status,
      classification,
      platformSubtype,
      notes,
      annotation_value,
      annotation_notes,
    },
  ) => {
    setActionLoadingState({ status, classification, platformSubtype })
    if (status && !isEmployee) {
      sendCustomerReportedBulkSlackMessage(
        sendSlackMessageMutation,
        rows,
        email,
        orgData,
        status,
      )
    }
    updateSpoofingReportsMutation({
      variables: {
        input: {
          report_ids: rows.map((row) => row.original.id),
          update_source: ReportChangeEventSource.BULK_UI,
          report_status: status,
          report_classification: classification,
          platform_subtype_id: platformSubtype?.id,
          notes,
          annotation_value,
          annotation_notes,
        },
      },
      update: (cache, { data: { update_reports_action } }) => {
        if (update_reports_action.updated_fields) {
          const fields = Object.fromEntries(
            Object.entries(update_reports_action.updated_fields).map(([key, value]) => {
              return [key, () => value]
            }),
          )
          for (const row of rows) {
            cache.modify({
              id: cache.identify(row.original),
              fields: fields,
            })
            if (platformSubtype) {
              cache.modify({
                id: cache.identify(row.original),
                fields: {
                  platform_subtype: () => platformSubtype,
                },
              })
            }
          }
        }
      },
    })
      .then(() => {
        setSelectedSpoofReport(null)
        setActionLoadingState(null)
        toast({
          title: `Success!`,
          description: `Updated ${rows.length} reports`,
          status: 'success',
          isClosable: true,
        })
      })
      .catch((e) => {
        toast({
          title: `Error updating reports`,
          description: `Error: ${e.message}`,
          status: 'error',
          isClosable: true,
        })
      })
      .finally(() => {
        // refetch the counts
        const queriesToRefetch = ['SearchSpoofingReportsCount']
        if (platformSubtype) {
          queriesToRefetch.push('SearchSpoofingReports')
        }
        delayRefetchedQueries(client, queriesToRefetch)
      })
  }

  useEffect(() => {
    if (isLoadingNextPage) {
      if (rows.length) {
        setSelectedSpoofReport(rows[0].original)
      }
      setIsLoadingNextPage(false)
    }
  }, [isLoadingNextPage])

  useEffect(() => {
    if (isLoadingPrevPage) {
      setSelectedSpoofReport(rows[selectedPageSize - 1].original)
      setIsLoadingPrevPage(false)
    }
  }, [isLoadingPrevPage])

  useEffect(() => {
    const currentIndex = rows.findIndex(
      (row) => selectedSpoofReport && row.original.id == selectedSpoofReport.id,
    )

    setSelectedRow(rows[currentIndex] ? rows[currentIndex].id : -1)
    setNextSelectedSpoofReport(
      currentIndex + 1 < rows.length && currentIndex >= 0
        ? rows[currentIndex + 1].original
        : null,
    )
  }, [selectedSpoofReport])

  const columns = useMemo(() => {
    let allColumns: ColumnConfig[] = []
    switch (selectedReportType) {
      case ReportType.SUSPICIOUS_EMAILS:
        allColumns = getSuspiciousEmailColumns(isEmployeeView)
        break
      case ReportType.DARK_WEB:
        allColumns = getDarkWebColumns(
          selectedReportStatus,
          selectedReportPlatform,
          isEmployeeView,
        )

        break
      default:
        allColumns = getSpoofingReportColumns(
          selectedReportType,
          selectedReportStatus,
          isOrgUnifiedView,
          orgId,
          isEmployeeView,
          orgData,
        )
    }

    const availableColumns = allColumns
      .filter((col) => {
        const isViewable = col.isColumnViewable ?? true
        const isVisible =
          fixedVisibleColumns.length === 0 || fixedVisibleColumns.includes(col.id)
        return isViewable && isVisible
      })
      .map(({ id, accessor, Cell, isColumnInitiallyHidden, sortType, filterType }) => ({
        id: id,
        accessor: accessor,
        Cell: Cell,
        Header: filterImprovementsEnabled ? (
          <HeaderMenu
            disableColumnHiding={fixedVisibleColumns.length > 0}
            filterType={filterType}
            id={id}
            onSortingChange={onSortingChange}
            sortType={sortType}
            sortingMechanism={sortingMechanism}
          />
        ) : (
          AlertTableColumnLabels[id] || id
        ),
        show: appSettings.alert_table_column_override[id] ?? !isColumnInitiallyHidden,
        disableSortBy: true,
      }))

    // Rearranging the columns because some are shared with dark web.
    const classificationIndex = availableColumns.findIndex(
      (col) => col.accessor === 'spoof_status',
    )

    // Find the index of the "Uploaded By" column
    const countryIndex = availableColumns.findIndex((col) => col.id === 'country')

    if (
      classificationIndex !== -1 &&
      countryIndex !== -1 &&
      classificationIndex > countryIndex
    ) {
      // Remove the "Classification" column from its current position
      const [classificationColumn] = availableColumns.splice(classificationIndex, 1)

      // Insert the "Classification" column before the "Uploaded By" column
      availableColumns.splice(countryIndex, 0, classificationColumn)
    }

    return availableColumns
  }, [
    selectedReportStatus,
    orgId,
    isEmployeeView,
    selectedReportPlatform,
    selectedReportType,
    appSettings,
    fixedVisibleColumns,
  ])

  const availableColumnIds = useMemo(() => {
    return columns.map((col) => col.id)
  }, [columns])
  const visibleColumnIds = useMemo(() => {
    return columns.filter((col) => col.show ?? true).map((col) => col.id)
  }, [columns])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page, // Instead of using 'rows', we'll use page,
    selectedFlatRows,
    rows,
    toggleAllRowsSelected,
    state: { pageIndex, pageSize },
  } = createTable(
    columns,
    data,
    selectedPage,
    selectedPageSize,
    totalPageCount,
    (row) => row.id,
    enableBulkActions, // Pass enableBulkActions to control checkbox visibility
    alertsTableResult?.rowSelectionState,
    (selectedRowIds) => {
      setAlertsTableResult((prev) => ({
        ...prev,
        rowSelectionState: selectedRowIds,
      }))
    },
  )

  // deselect all rows when the view changes
  useEffect(() => {
    if (isComponentMountingRef.current) {
      isComponentMountingRef.current = false
    } else {
      toggleAllRowsSelected(false)
    }
  }, [selectedReportStatus, selectedReportPlatform, selectedReportType])

  const pageSizeOptions = isEmployeeView ? [30, 50, 100, 200, 500] : [30, 50, 100]

  return (
    <VStack>
      {bulkActionsEnabled && (
        <SpoofReportsTableActions
          actionLoadingState={actionLoadingState}
          isOrgUnifiedView={isOrgUnifiedView}
          selectedRows={selectedFlatRows}
          updateReports={bulkUpdateReports}
        />
      )}

      <PaginatedTable
        getTableBodyProps={getTableBodyProps}
        getTableProps={getTableProps}
        headerGroups={headerGroups}
        loading={false}
        page={page}
        pageIndex={pageIndex}
        pageSize={pageSize}
        pageSizeOptionsOverride={pageSizeOptions}
        postHeaderContent={
          // columns that the user should not have access to in the view, if this is not empty do not show the column edit dropdown
          fixedVisibleColumns.length === 0 ? (
            <SpoofReportsTableColumnEditDropdown
              availableColumnIds={availableColumnIds}
              visibleColumnIds={visibleColumnIds}
            />
          ) : null
        }
        prepareRow={prepareRow}
        selectedPage={selectedPage}
        selectedRow={selectedRow}
        setSelectedObj={handleSelectAlert}
        setSelectedPage={setSelectedPage}
        setSelectedPageSize={setSelectedPageSize}
        style={style}
        totalPageCount={totalPageCount}
      />
    </VStack>
  )
}

export default withApollo<any>(SpoofReportsTableContent)
